import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, Building, Calendar, Tag, MessageSquare, RefreshCw, LogOut, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = ({ user }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact`, {
        withCredentials: true
      });
      setSubmissions(response.data);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (service) => {
    const labels = {
      'phone-attendant': 'Automated Phone Attendant',
      'cx-automation': 'Customer Experience Automation',
      'social-media': 'Social Media Management',
      'website': 'Website Design',
      'other': 'Other / Not Sure'
    };
    return labels[service] || service;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'contacted': 'bg-blue-100 text-blue-800',
      'converted': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <section className="section">
        <div className="container mx-auto px-4">
          {/* User Info Bar */}
          {user && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#227AFF]/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-[#227AFF]" />
                  </div>
                )}
                <div>
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          )}

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Contact Submissions</h1>
              <p className="text-gray-600">Manage and track all contact form submissions</p>
            </div>
            <button
              onClick={fetchSubmissions}
              disabled={loading}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading && !submissions.length ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#227AFF] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
              <p className="text-gray-600">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-1">{submission.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                          {submission.status}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(submission.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-[#227AFF] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{submission.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Phone className="h-5 w-5 text-[#227AFF] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium">{submission.phone}</p>
                      </div>
                    </div>

                    {submission.company && (
                      <div className="flex items-start space-x-3">
                        <Building className="h-5 w-5 text-[#227AFF] mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Company</p>
                          <p className="font-medium">{submission.company}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start space-x-3">
                      <Tag className="h-5 w-5 text-[#227AFF] mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Service Interest</p>
                        <p className="font-medium">{getServiceLabel(submission.service)}</p>
                      </div>
                    </div>
                  </div>

                  {submission.message && (
                    <div className="flex items-start space-x-3 bg-gray-50 rounded-lg p-4">
                      <MessageSquare className="h-5 w-5 text-[#227AFF] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 mb-1">Message</p>
                        <p className="text-gray-700">{submission.message}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {submissions.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-500">
              Total submissions: {submissions.length}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
